/**
 * AD Domain Information queries - understanding domain structure and membership.
 */

import { QueryFolders } from '@/sections/insights/queryBuilder/savedQueries/folders';
import type { SavedQuery } from '@/sections/insights/queryBuilder/types/SavedQuery';

import { DOMAIN_USERS_DIRECT_PERMISSIONS_RELATIONSHIPS } from './relationships';

export const AD_DOMAIN_INFO_QUERIES: SavedQuery[] = [
  {
    id: 'all-domain-admins',
    name: 'All Domain Admins',
    type: 'table',
    description:
      'Find all users and computers who are members of Domain Admins groups through any number of group memberships.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADObject'],
        filters: [
          {
            field: '',
            operator: 'OR',
            value: [
              { field: 'class', operator: '=', value: 'user' },
              { field: 'class', operator: '=', value: 'computer' },
            ],
          },
        ],
        relationships: [
          {
            label: 'MemberOf',
            optional: false,
            length: -1,
            target: {
              labels: ['ADGroup'],
              filters: [
                {
                  field: 'objectid',
                  operator: 'ENDS WITH',
                  value: '-512',
                  not: false,
                },
              ],
              relationships: [],
            },
          },
        ],
      },
      limit: 200,
    },
  },
  {
    id: 'domain-users-direct-permissions',
    name: 'Domain Users Direct Permissions',
    type: 'graph',
    description:
      'Shows what the Domain Users group has direct access to across the domain. Since all authenticated users are members of Domain Users, this reveals the baseline permissions available to any domain account.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADGroup'],
        filters: [
          {
            field: 'objectid',
            operator: 'ENDS WITH',
            value: '-513',
            not: false,
          },
        ],
        relationships: [
          {
            label: [...DOMAIN_USERS_DIRECT_PERMISSIONS_RELATIONSHIPS],
            optional: false,
            length: 1,
            target: {
              labels: ['ADObject'],
              filters: [],
              relationships: [],
            },
          },
        ],
      },
      limit: 200,
    },
  },
  {
    id: 'adminsdholder-protected-objects',
    name: 'AdminSDHolder Protected Objects',
    type: 'graph',
    description:
      'Find all objects protected by AdminSDHolder and their organizational placement within the domain.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADDomain'],
        filters: [],
        relationships: [
          {
            label: 'Contains',
            optional: false,
            length: -1,
            target: {
              labels: ['ADObject'],
              filters: [
                {
                  field: 'adminsdholderprotected',
                  operator: '=',
                  value: true,
                  not: false,
                },
              ],
              relationships: [],
            },
          },
        ],
      },
      limit: 200,
      shortest: 1,
    },
  },
  {
    id: 'map-domain-trusts',
    name: 'Map Domain Trusts',
    type: 'graph',
    description:
      'Visualize trust relationships between domains, including same-forest and cross-forest trusts.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADDomain'],
        filters: [],
        relationships: [
          {
            label: ['SameForestTrust', 'CrossForestTrust'],
            optional: false,
            length: 1,
            target: {
              labels: ['ADDomain'],
              filters: [],
              relationships: [],
            },
          },
        ],
      },
      limit: 200,
    },
  },
  {
    id: 'map-ou-structure',
    name: 'Map OU Structure',
    type: 'graph',
    description:
      'Visualize the complete Organizational Unit hierarchy within the domain.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADDomain'],
        filters: [],
        relationships: [
          {
            label: 'Contains',
            optional: false,
            length: -1,
            target: {
              labels: ['ADOU'],
              filters: [],
              relationships: [],
            },
          },
        ],
      },
      limit: 200,
    },
  },
  {
    id: 'tier-zero-object-locations',
    name: 'Locations of Tier Zero / High Value Objects',
    type: 'graph',
    description:
      "Find all AD objects tagged as 'tier-zero' and their containment path within the domain hierarchy.",
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADDomain'],
        filters: [],
        relationships: [
          {
            label: 'Contains',
            optional: false,
            length: -1,
            target: {
              labels: ['ADObject'],
              filters: [
                {
                  field: 'tags',
                  operator: 'IN',
                  value: 'tier-zero',
                  reverseOperands: true,
                  not: false,
                },
              ],
              relationships: [],
            },
          },
        ],
      },
      limit: 200,
      shortest: 1,
    },
  },
];
